/* =============================================================
   Pre-Screening — production one-pager script
   All UI copy lives in the HTML (templates + data-* attributes).
   This file owns: eligibility engine + DOM rendering.
   Lender data is imported from ./data/panel-lenders.
   ============================================================= */

//import { PANEL_LENDERS } from './data/panel-lenders';

(function () {
  'use strict';

  const PANEL_LENDERS = (typeof window !== 'undefined' && Array.isArray(window.PANEL_LENDERS))
    ? window.PANEL_LENDERS
    : [];

  /* ---------- Eligibility engine ---------- */
  function incomeBracket(income) {
    if (income <= 1500) return 0;
    if (income <= 3000) return 1;
    if (income <= 5000) return 2;
    if (income <= 9999) return 3;
    if (income <= 14999) return 4;
    return 5;
  }

  function evalLender(lender, ctx) {
    // Credit cards: eligibility is min-income only (no emp/age/DSR/loan math).
    if (lender.group === 'creditcard') {
      return { pass: ctx.income >= (+lender.minIncome || 0), reason: 'income', creditCard: true };
    }
    if (lender.emp[ctx.emp] !== 'PASS') return { pass: false, reason: 'emp' };
    const bk = incomeBracket(ctx.income);
    if (lender.income[bk] !== 'PASS') return { pass: false, reason: 'income' };
    if (ctx.age < lender.ageMin || ctx.age > lender.ageMax) return { pass: false, reason: 'age' };

    const dsrCell = lender.dsr[bk];
    let monthlyCap, dsrUsed;
    if (dsrCell === 'FAIL') return { pass: false, reason: 'dsr' };
    if (dsrCell === 'NA') {
      monthlyCap = ctx.income - ctx.commitment;
      dsrUsed = null;
    } else {
      monthlyCap = (ctx.income * dsrCell) - ctx.commitment;
      dsrUsed = dsrCell;
    }
    if (monthlyCap < 300) return { pass: false, reason: 'cap' };

    if (lender.rate === 'NA' || lender.maxTen === 'NA' || lender.maxLoan === 'NA') {
      return { pass: true, referralOnly: true, monthlyCap, dsrUsed };
    }

    const tenureCap = Math.min(lender.maxTen, lender.ageMax - ctx.age);
    if (tenureCap < 1) return { pass: false, reason: 'tenure' };

    const totalPay = monthlyCap * 12 * tenureCap;
    const factor = (1 + lender.rate * tenureCap);
    let eligibleLoan = Math.min(Math.floor(totalPay / factor / 1000) * 1000, lender.maxLoan);
    if (eligibleLoan < 1000) return { pass: false, reason: 'cap' };
    const installment = Math.round(eligibleLoan * factor / (tenureCap * 12));

    return { pass: true, eligibleLoan, tenure: tenureCap, installment, monthlyCap, dsrUsed };
  }

  /* ---------- Format helpers (data only) ---------- */
  const fmtRM = n => 'RM ' + Number(n).toLocaleString('en-MY');

  /* ---------- DOM helpers ---------- */
  function $(id) { return document.getElementById(id); }
  function tpl(id) {
    const t = $(id);
    return t ? t.content.cloneNode(true) : document.createDocumentFragment();
  }
  function fillSlot(node, slot, value) {
    node.querySelectorAll('[data-slot="' + slot + '"]').forEach(el => {
      el.textContent = value;
    });
  }
  function showSlot(node, slot, show) {
    node.querySelectorAll('[data-slot="' + slot + '"]').forEach(el => {
      el.hidden = !show;
    });
  }

  /* ---------- Type tabs (order matters: shown left-to-right) ---------- */
  const TYPE_ORDER = ['bank', 'kpkt', 'advisory', 'p2p', 'creditcard'];

  /* ---------- State ---------- */
  let activeType = 'bank';

  /* ---------- Inputs ---------- */
  function readCtx() {
    // Guard each field: a control may be absent if its options aren't configured
    // (e.g. the employment select renders only when ACF has employment rows).
    return {
      income: +($('psIncome')?.value || 0),
      age: +($('psAge')?.value || 0),
      commitment: +($('psCommit')?.value || 0),
      emp: $('psEmp')?.value ?? ''
    };
  }

  function updateInputLabels() {
    $('psIncomeVal').textContent = fmtRM(+$('psIncome').value);
    $('psAgeVal').textContent = +$('psAge').value;
    $('psCommitVal').textContent = fmtRM(+$('psCommit').value);
  }

  function selectedEmpShort() {
    const sel = $('psEmp');
    if (!sel) return '';
    const opt = sel.options[sel.selectedIndex];
    return opt ? (opt.dataset.short || opt.text) : '';
  }

  /* ---------- Best-of helper ---------- */
  function pickBest(arr) {
    if (arr.length === 0) return null;
    const calc = arr.filter(x => !x.r.referralOnly);
    if (calc.length > 0) {
      const top = calc.reduce((a, b) => (b.r.eligibleLoan > a.r.eligibleLoan ? b : a));
      return { ref: false, r: top.r, dsr: top.r.dsrUsed, cap: top.r.monthlyCap };
    }
    return { ref: true, r: arr[0].r, dsr: arr[0].r.dsrUsed, cap: arr[0].r.monthlyCap };
  }

  /* ---------- Eligibility panel ---------- */
  function buildPanel(passL1, passL2, totalCount) {
    if (passL1.length === 0 && passL2.length === 0) {
      return tpl('psTplPanelLow');
    }
    const high = passL1.length > 0;
    const node = tpl(high ? 'psTplPanelHigh' : 'psTplPanelMed');
    const best = pickBest(high ? passL1 : passL2);

    if (best.ref) {
      fillSlot(node, 'loan', $('psResult').dataset.labelReferral || '');
      fillSlot(node, 'tenure', '—');
      fillSlot(node, 'monthly', '—');
    } else {
      fillSlot(node, 'loan', fmtRM(best.r.eligibleLoan));
      fillSlot(node, 'tenure', best.r.tenure);
      fillSlot(node, 'monthly', fmtRM(best.r.installment) + '/mo');
    }

    fillSlot(node, 'emp', selectedEmpShort());

    if (high) {
      fillSlot(node, 'match', passL1.length + passL2.length);
      fillSlot(node, 'total', totalCount);
      fillSlot(node, 'dsr', best.dsr ? Math.round(best.dsr * 100) : '—');
      fillSlot(node, 'cap', best.cap ? fmtRM(Math.round(best.cap)) : '—');
    } else {
      fillSlot(node, 'match', passL2.length);
    }

    return node;
  }

  /* ---------- Lender row ---------- */
  function buildLenderRow({ L, r }) {
    const node = tpl('psTplLenderRow');

    // Apply button — accept either the legacy [data-drawer="apply"] hook
    // or the new lightbox button [data-form] (whichever the template uses).
    const applyBtn = node.querySelector('[data-form], [data-drawer="apply"], .btn-form-open');
    if (applyBtn) {
      applyBtn.dataset.code = L.code;
      applyBtn.dataset.lenderName = L.name;
      if (L.url) applyBtn.dataset.form = L.url;
    }

    const logo = node.querySelector('[data-slot="logo"]');
    if (logo && L.logo) {
      logo.src = L.logo.url;
      logo.alt = L.name;
      if (L.logo.width) logo.width = L.logo.width;
      if (L.logo.height) logo.height = L.logo.height;
    }

    fillSlot(node, 'name', L.name);
    fillSlot(node, 'p_name', L.p_name || '');

    if (L.rate === 'NA') {
      showSlot(node, 'rate-na', true);
    } else {
      showSlot(node, 'rate-calc', true);
      fillSlot(node, 'rate-num', (L.rate * 100).toFixed(2));
    }

    if (r.referralOnly) {
      showSlot(node, 'amount-referral', true);
      showSlot(node, 'tenure-na', true);
      showSlot(node, 'inst-na', true);
    } else {
      showSlot(node, 'amount', true);
      fillSlot(node, 'amount', fmtRM(r.eligibleLoan));
      showSlot(node, 'tenure-calc', true);
      fillSlot(node, 'tenure-num', r.tenure);
      showSlot(node, 'inst-calc', true);
      fillSlot(node, 'inst-num', fmtRM(r.installment));
    }

    return node;
  }

  /* ---------- Credit-card row (Card | Min income | Annual fee | Cashback | Apply) ---------- */
  function buildCreditCardRow({ L }) {
    const node = tpl('psTplCreditCardRow');

    const applyBtn = node.querySelector('[data-form], [data-drawer="apply"], .btn-form-open');
    if (applyBtn) {
      applyBtn.dataset.code = L.code || '';
      applyBtn.dataset.lenderName = L.name;
      applyBtn.dataset.value = L.name;
      if (L.url) applyBtn.dataset.form = L.url;
    }

    const logo = node.querySelector('[data-slot="logo"]');
    if (logo && L.logo) {
      logo.src = L.logo.url;
      logo.alt = L.name;
      if (L.logo.width) logo.width = L.logo.width;
      if (L.logo.height) logo.height = L.logo.height;
    }

    fillSlot(node, 'name', L.name);
    fillSlot(node, 'p_name', L.p_name || '');
    fillSlot(node, 'min-income', fmtRM(+L.minIncome || 0) + '/mo');
    fillSlot(node, 'annual-fee', L.annualFee || '—');
    fillSlot(node, 'cashback', L.cashback || '—');

    return node;
  }

  /* ---------- Lender results (tabs + table or empty) ---------- */
  function buildLenderResults(passByType, totalByType) {
    const section = tpl('psTplLenders');

    const lendersEl = section.querySelector('.ps-lenders');
    const labels = {
      bank: lendersEl.dataset.tabBank,
      kpkt: lendersEl.dataset.tabKpkt,
      advisory: lendersEl.dataset.tabAdvisory,
      p2p: lendersEl.dataset.tabP2p,
      creditcard: lendersEl.dataset.tabCreditcard
    };

    // Auto-pick a sensible active tab if the current one has nothing.
    if (!passByType[activeType] || passByType[activeType].length === 0) {
      const fallback = TYPE_ORDER.find(t => passByType[t] && passByType[t].length > 0);
      if (fallback) activeType = fallback;
    }

    // Build tabs.
    const tabsEl = section.querySelector('[data-slot="tabs"]');
    TYPE_ORDER.forEach(typeKey => {
      const passCount = (passByType[typeKey] || []).length;
      const totalForType = totalByType[typeKey] || 0;
      if (!totalForType) return; // hide tabs with no products

      const tab = tpl('psTplLenderTab');
      fillSlot(tab, 'label', labels[typeKey] || typeKey);
      fillSlot(tab, 'pass', passCount);
      fillSlot(tab, 'total', totalForType);

      const btn = tab.querySelector('[data-action="select-type"]');
      btn.dataset.type = typeKey;
      if (typeKey === activeType) btn.classList.add('is-active');
      if (passCount === 0) btn.classList.add('is-empty');

      tabsEl.appendChild(tab);
    });

    // Build body: either the lender table or an empty-state message.
    const bodyEl = section.querySelector('[data-slot="body"]');
    const view = passByType[activeType] || [];

    if (view.length === 0) {
      const empty = tpl('psTplLendersEmpty');
      fillSlot(empty, 'type', labels[activeType] || activeType);
      bodyEl.appendChild(empty);
    } else {
      const isCreditCard = activeType === 'creditcard';
      const table = tpl(isCreditCard ? 'psTplCreditCardTable' : 'psTplLenderTable');
      const rowsEl = table.querySelector('[data-slot="rows"]');
      const rowFn = isCreditCard ? buildCreditCardRow : buildLenderRow;
      view.forEach(item => rowsEl.appendChild(rowFn(item)));
      bodyEl.appendChild(table);
    }

    return section;
  }

  /* ---------- Unavailable expandable ---------- */
  function buildUnavail(failed, typeLabel) {
    if (failed.length === 0) return document.createDocumentFragment();

    const groups = {};
    failed.forEach(({ L, r }) => {
      const key = r.reason || 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(L.name);
    });

    const node = tpl('psTplUnavail');
    fillSlot(node, 'count', failed.length);
    fillSlot(node, 'type', typeLabel);

    const tbody = node.querySelector('[data-slot="rows"]');
    const labels = tbody.parentElement.dataset; // reason labels live on <table>

    Object.keys(groups).forEach(key => {
      const row = tpl('psTplUnavailRow');
      const labelKey = 'label' + key.charAt(0).toUpperCase() + key.slice(1);
      fillSlot(row, 'label', labels[labelKey] || key);
      fillSlot(row, 'codes', groups[key].join(', '));
      fillSlot(row, 'count', groups[key].length);
      tbody.appendChild(row);
    });

    return node;
  }

  /* ---------- Tab labels (read once from the template) ---------- */
  function getTypeLabels() {
    const tplEl = $('psTplLenders');
    if (!tplEl) return {};
    const lenders = tplEl.content.querySelector('.ps-lenders');
    if (!lenders) return {};
    return {
      bank: lenders.dataset.tabBank,
      kpkt: lenders.dataset.tabKpkt,
      advisory: lenders.dataset.tabAdvisory,
      p2p: lenders.dataset.tabP2p,
      creditcard: lenders.dataset.tabCreditcard
    };
  }

  /* ---------- Type bucketing ---------- */
  function bucketByType(evals) {
    // Total per type (panel composition, irrespective of pass/fail).
    const totalByType = {};
    PANEL_LENDERS.forEach(L => {
      totalByType[L.group] = (totalByType[L.group] || 0) + 1;
    });

    // Passing per type.
    const passByType = {};
    TYPE_ORDER.forEach(t => { passByType[t] = []; });
    evals.forEach(item => {
      if (!item.r.pass) return;
      const key = item.L.group;
      if (!passByType[key]) passByType[key] = [];
      passByType[key].push(item);
    });

    return { totalByType, passByType };
  }

  /* ---------- Main render ---------- */
  function renderResults() {
    updateInputLabels();
    const ctx = readCtx();
    const evals = PANEL_LENDERS.map(L => ({ L, r: evalLender(L, ctx) }));
    const passL1 = evals.filter(x => x.r.pass && x.L.layer === 1);
    const passL2 = evals.filter(x => x.r.pass && x.L.layer === 2);
    const failed = evals.filter(x => !x.r.pass);
    const { totalByType, passByType } = bucketByType(evals);
    // Loan summary panel counts loans only; the tabs/table block shows whenever
    // ANY group matches (so the Credit Cards tab appears even with no loan match).
    const loanTotal = PANEL_LENDERS.filter(L => L.group !== 'creditcard').length;
    const anyPassing = evals.some(x => x.r.pass);

    const target = $('psResult');
    target.replaceChildren();

    target.appendChild(buildPanel(passL1, passL2, loanTotal));

    const labels = getTypeLabels();

    if (!anyPassing) {
      // No tabs visible — show all failures, scoped under the current default tab.
      target.appendChild(buildUnavail(failed.filter(x => x.L.group === activeType), labels[activeType] || activeType));
      return;
    }

    target.appendChild(buildLenderResults(passByType, totalByType));

    // Unavailable list scoped to the active tab — buildLenderResults may have
    // auto-switched activeType, so read it after that call.
    const failedForActive = failed.filter(x => x.L.group === activeType);
    target.appendChild(buildUnavail(failedForActive, labels[activeType] || activeType));
  }

  /* ---------- Init ---------- */
  function init() {
    const result = $('psResult');
    if (!result) return;

    ['psIncome', 'psAge', 'psCommit', 'psEmp'].forEach(id => {
      const el = $(id);
      if (!el) return;
      el.addEventListener('input', renderResults);
      el.addEventListener('change', renderResults);
    });

    result.addEventListener('click', (ev) => {
      const tab = ev.target.closest('[data-action="select-type"]');
      if (tab && tab.dataset.type && tab.dataset.type !== activeType) {
        ev.preventDefault();
        activeType = tab.dataset.type;
        renderResults();
      }
    });

    renderResults();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();